<!--

Copyright Paul B. Lucas (C) 2025  ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component isused when user is in a Receiver role.  It allows the receiver to create an asset in the database
entering the FabWeb+ data fieldset.   Assets so recovered are market 'Removed-from-Service'

Revisions:
    03/07/2025      pblucas     initial creation


-->
<template>
    <div class="_master_container"> 
        <!--
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        |
        |   RENDERS UNIT LIST FOR ALLOWED ORGS WITH FILTERS.  SELECTION IS VIA LINK/ACTION BUTTON
        |   Selection sets steward-netid for subsequent actions
        |
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        -->
        <div v-if="renderState == 'SELECT-UNIT'">
            <p class="_p_instruction_header">Select an unit from which assets are to be recovered</p>
    
            <div v-if="filteredUnits?.length  && filteredUnits.length > 0">
                <div class="_list">
                    <p class="_p_title_header">Units for {{ orgDisplay }}</p>
                </div>
                <div class="_scrollable_list_container">
                    <div class="_table">
                        <!-- table row for list filters -->
                        <div class="_table_fixed_row" style="top: 0;">
                             <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="unitFilters['unit']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="unitFilters['short-name']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="unitFilters['netid']" @keyup="setFilters">
                            </div> 
                             <div class="_table_cell">
                                <button class="_button_clear_filters" @click="clearUnitFilters">Clear Filters</button>
                            </div>
                        </div>

                        <!-- table for for header names -->
                        <div class="_table_fixed_row" style="top: 25px;">
                            <div class="_table_cell"> 
                                <p class="_p_list_header">Unit Name</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Short Name</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Unit Identifier</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- table row for listed data -->
                        <div class="_table_row" v-for="(_,idx) in filteredUnits" :key="filteredUnits[idx]['short-name']" >
                            <div class="_table_link_cell" style="text-align: left;" 
                                tabindex="0"
                                aria-label="Action link to select unit in list"  
                                aria-describedby="Link on unit name to select unit for deployment"  
                                @click="selectUnit(filteredUnits[idx], idx)"
                                @keydown.enter="selectUnit(filteredUnits[idx],idx)">
                                {{ filteredUnits[idx]['unit'] }}
                            </div>
                            <div class="_table_cell" style="text-align: left;">
                                {{ filteredUnits[idx]['short-name'] }}
                            </div>                
                            <div class="_table_cell">
                                {{ filteredUnits[idx]['unit-id'] }}
                            </div>
                            <div class="_actions_table_cell">
                                <button class="_action_button" style="order:1;" 
                                    tabindex="0"
                                    aria-label="Action to select unit"  
                                    aria-describedby="Inline button to select unit in list row selected" 
                                    @click="selectUnit(filteredUnits[idx], idx)"
                                    @keydown.enter="selectUnit(filteredUnits[idx], idx)">
                                    Select Unit</button>
                                
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
            <div class="_no_assets_warning_container" v-else>
                <p class="_assets_warning" style="order: 1;">
                    There are no assets issued to the selected unit
                </p>
                <div class="_assets_warning_clear" style="order: 2;">
                    <button class="_main_button" 
                        tabindex="0"
                        aria-label="Clears filter list" 
                        aria-decribedby="Allow user option of clearing filters when no results show on current filters" 
                        @click="resetAssetFilters"
                        @keydown.enter="resetAssetFilters">Clear Filters</button>
                </div> 

            </div>      
        </div>
       <!--
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        |
        |   RENDERS ISSUED ASSET LIST FOR UNIT WITH FILTERS.  ACTIONS: EDIT/VIEW/SELECT ON CHECKBOX
        |
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        -->
        <!-- spin until data if loaded from database -->
        <div v-if="renderState == 'LIST-ISSUED-ASSETS'">
            <div v-if="!bAssetsLoaded">
                <p class="_p_patience"> Procesing assets selected</p>
                <SemipolarSpinner class="_spinner"
                :animation-duration="2000"
                :size="65"
                color="#13294B"/>
            </div>
        </div>
        <div v-if="renderState == 'LIST-ISSUED-ASSETS'">
            <div v-if="filteredAssets.length > 0">
                <div class="_list">
                    <p class="_p_list_header">Assets selected for recovery from {{ unit['short-name'] }}</p>
                </div>
                <div class="_scrollable_list_container">
                    <div class="_table">
                        <!-- asset filter entry -->
                        <div class="_table_fixed_row" style="top: 0;">
                            <div class="_table_cell">
                                &nbsp;
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['tag']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['asset-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['owner-org']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['it-data']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['serial-no']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['description']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['mfg']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['model']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['os-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <button class="_button_clear_filters" @click="clearAssetFilters">Clear Filters</button>
                            </div> 
                        </div>
                        <!-- asset table headers -->
                        <div class="_table_fixed_row" style="top: 25px;">
                            <div class="_table_cell">
                                <p class="_p_list_header">Selected</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Tag</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Asset Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Owner Org</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">IT Data</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Serial No</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Description</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Manufacturer</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Model</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">OS Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- asset property values -->
                        <div class="_table_row" v-for="(asset,idx) in filteredAssets" :key="filteredAssets[idx].uuid">
                            <div class="_table_check_cell">
                                <input class="_check" type="checkbox" v-model="filteredAssets[idx].selected" 
                                    tabindex="0"
                                    aria-label="Check to include in deployed assets" 
                                    aria-describedby="Allows user to select assets for deployment"  />
                            </div>
                            <div class="_table_link_cell" style="text-align: left;" 
                                tabindex="0"
                                aria-label="Action link to view selected asset in list"  
                                aria-describedby="Link on assets tag to open view page on asset selected in list row"  
                                @click="viewIssuedAsset(filteredAssets[idx], idx)"
                                @keydown.enter="viewIssuedAsset(filteredAssets[idx],idx)">
                                {{ filteredAssets[idx]['tag']}}
                            </div>
                            <div class="_table_cell">
                                {{ prettyType(filteredAssets[idx]['asset-type'])}}
                            </div>
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['owner-org'] }}
                            </div>  
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['it-data'] }}
                            </div>              
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['serial-no'] }}
                            </div>  
                            <div class="_table_cell_ellipsis">
                                {{ filteredAssets[idx].description }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx].mfg }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx].model }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx]['os-type'] }}
                            </div>
                            <div class="_actions_table_cell">
                                <button class="_action_button" style="order:1;" 
                                    tabindex="0" 
                                    aria-label="View asset in list row for deployment" 
                                    aria-describedby="View asset for deployment" 
                                    @click.stop="viewIssuedAsset(filteredAssets[idx], idx)"
                                    @keydown.enter.stop="viewIssuedAsset(filteredAssets[idx], idx)">View Asset</button>
                              
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
        </div>
        <div class="_no_assets_warning_container" v-if="filteredAssets.length == 0 && bAssetsLoaded">
            <p class="_assets_warning" style="order: 1;">
                There are no issued assets for unit {{ unit['short-name'] }}
            </p>
            <div class="_assets_warning_clear" style="order: 2;">
                <button class="_main_button" 
                    tabindex="0"
                    aria-label="Clears filter list" 
                    aria-decribedby="Allow user option of clearing filters when no results show on current filters" 
                    @click="clearAssetFilters"
                    @keydown.enter="clearAssetFilters">Clear Filters</button>
            </div> 
        </div>      

        <div class="_instructions" v-if="renderState == 'LIST-ISSUED-ASSETS'">
            <p class="_p_instructions">
                The above assets issued to {{ unit['short-name']}} as custodian/steward  
                are available to be recovered.    Assets selected will be recovered to inventory and marked as
                'Removed from Service'.    Removed assets can be reimaged or otherwise cleaned and
                returned to the deployable inventory or may be surplused.  See 'Disposition' tab.
            </p>
        
        </div>

               <!--
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        |
        |   RENDERS SELECTED ASSET LIST FOR UNIT WITH FILTERS.  ACTIONS: EDIT/VIEW
        |
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        -->
        <!-- spin until data if loaded from database -->
        <div v-if="renderState == 'LIST-SELECTED-ASSETS'">
            <div v-if="!bAssetsLoaded">
                <p class="_p_patience"> Loading assets from database - be patient!</p>
                <SemipolarSpinner class="_spinner"
                :animation-duration="2000"
                :size="65"
                color="#13294B"/>
            </div>
        </div>
        <div v-if="renderState == 'LIST-SELECTED-ASSETS'">
            <div v-if="filteredAssets.length > 0">
                <div class="_list">
                    <p class="_p_list_header">Assets selected for recovery from {{ unit['short-name'] }}</p>
                </div>
                <div class="_scrollable_list_container">
                    <div class="_table">
                        <!-- asset filter entry -->
                        <div class="_table_fixed_row" style="top: 0;">
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['tag']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['asset-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['owner-org']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['it-data']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['serial-no']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['description']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['mfg']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['model']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['os-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <button class="_button_clear_filters" @click="clearAssetFilters">Clear Filters</button>
                            </div> 
                        </div>
                        <!-- asset table headers -->
                        <div class="_table_fixed_row" style="top: 25px;">
                            <div class="_table_cell">
                                <p class="_p_list_header">Tag</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Asset Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Owner Org</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">IT Data</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Serial No</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Description</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Manufacturer</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Model</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">OS Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- asset property values -->
                        <div class="_table_row" v-for="(asset,idx) in filteredSelectedAssets" :key="filteredSelectedAssets[idx].uuid">
    
                            <div class="_table_link_cell" style="text-align: left;" 
                                tabindex="0"
                                aria-label="Action link to view selected asset in list"  
                                aria-describedby="Link on assets tag to open view page on asset selected in list row"  
                                @click="viewSelectedAsset(filteredAssets[idx], idx)"
                                @keydown.enter="viewSelectedAsset(filteredAssets[idx],idx)">
                                {{ filteredAssets[idx]['tag']}}
                            </div>
                            <div class="_table_cell">
                                {{ prettyType(filteredAssets[idx]['asset-type'])}}
                            </div>
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['owner-org'] }}
                            </div>  
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['it-data'] }}
                            </div>              
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['serial-no'] }}
                            </div>  
                            <div class="_table_cell_ellipsis">
                                {{ filteredAssets[idx].description }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx].mfg }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx].model }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx]['os-type'] }}
                            </div>
                            <div class="_actions_table_cell">
                                <button class="_action_button" style="order:1;" 
                                    tabindex="0" 
                                    aria-label="View asset in list row for deployment" 
                                    aria-describedby="View asset for deployment" 
                                    @click.stop="viewSelectedAsset(filteredAssets[idx], idx)"
                                    @keydown.enter.stop="viewSelectedAsset(filteredAssets[idx], idx)">View Asset</button>
                              
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
        </div>
        <div class="_no_assets_warning_container" v-if="filteredAssets.length == 0 && bAssetsLoaded">
            <p class="_assets_warning" style="order: 1;">
                There are no issued assets for unit {{ unit['short-name'] }}
            </p>
            <div class="_assets_warning_clear" style="order: 2;">
                <button class="_main_button" 
                    tabindex="0"
                    aria-label="Clears filter list" 
                    aria-decribedby="Allow user option of clearing filters when no results show on current filters" 
                    @click="clearAssetFilters"
                    @keydown.enter="clearAssetFilters">Clear Filters</button>
            </div> 
        </div>      
        <!-- 
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        |
        |       VIEW EDIT ASSET FORM RENDERING
        |
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>   
        -->
    
        <div v-if="renderState == 'VIEW-ISSUED-ASSET'">
            <ViewAssetEntire :asset="filteredAssets[viewIndex]" 
                             :key="filteredAssets[viewIndex].uuid"/>
        </div>
        <div v-if="renderState == 'VIEW-SELECTED-ASSET'">
            <ViewAssetEntire :asset="filteredSelectedAssets[viewIndexSelected]" 
                             :key="filteredSelectedAssets[viewIndexSelected].uuid"/>
        </div>

        <div class="_page_controls">
            <!-- allow user to rescind employee selection -->
            <button class="_main_button"   type="button" v-if="renderState != 'SELECT-UNIT'"
                    tabindex="0"
                    aria-label="Re-Select Unit" 
                    aria-describedby="Control to reset form to new unit"
                    @click="resetUnitSelection()"
                    @keydown.enter="resetUnitSelection()">Reset Unit Selection
            </button>

            <button class="_main_button"   type="button" v-if="renderState == 'LIST-ISSUED-ASSETS' && !bNoAssetsFound"
                    tabindex="0"
                    aria-label="Use selected assets" 
                    aria-describedby="Control to use selected assets"
                    @click="useSelectedAssets()"
                    @keydown.enter="useSelectedAssets()">Continue
            </button>
            <!-- allow user to rescind asset selection -->
            <button class="_main_button"   type="button" v-if="renderState == 'LIST-SELECTED-ASSETS'"
                    tabindex="0"
                    aria-label="Reset selected assets" 
                    aria-describedby="Control to reset selected assets"
                    @click="unselectAssets()"
                    @keydown.enter="unselectAssets()">Reselect Assets
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'LIST-SELECTED-ASSETS'"
                    tabindex="0"
                    aria-label="Recover selected assets" 
                    aria-describedby="Control to recover selectd assets"
                    @click="recoverAssets()"
                    @keydown.enter="recoverAssets()">Recover Checked Assets
            </button>

            <div v-if="renderState == 'LIST-SELECTED-ASSETS'  && bRecoveryComplete">
                <p class="_issue_notice">Assets recovered from {{ unit['short-name'] }}</p>
            </div>

            <!-- viewing assets -->
            <button class="_main_button"   type="button" v-if="renderState == 'VIEW-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="view asset" 
                    aria-describedby="Control to view asset"
                    @click="nextSelectedAsset()"
                    @keydown.enter="nextSelectedAsset()">Next
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'VIEW-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="Cancel and return to list" 
                    aria-describedby="Cancel and return to selected asset list"
                    @click="backToSelectedAssetList()"
                    @keydown.enter="backToSelectedAssetList()">Return to List
            </button>
           <button class="_main_button"   type="button" v-if="renderState == 'VIEW-ISSUED-ASSET'"
                    tabindex="0"
                    aria-label="view asset" 
                    aria-describedby="Control to view asset"
                    @click="nextAsset()"
                    @keydown.enter="nextAsset()">Next
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'VIEW-ISSUED-ASSET'"
                    tabindex="0"
                    aria-label="Cancel and return to list" 
                    aria-describedby="Cancel and return to selected asset list"
                    @click="backToAssetList()"
                    @keydown.enter="backToAssetList()">Return to List
            </button>

        </div>


    </div>
    
</template>

<script>
    import {ref, computed}              from 'vue'
    import {useStore}                   from 'vuex'
    
    import EditAsset                    from '@/components/Assets/EditAsset.vue'
    import ViewAssetEntire              from '@/components/Assets/ViewAssetEntire.vue'
    import { SemipolarSpinner }         from 'epic-spinners'

 
    // import composables
    import useAssetsAPI                 from '@/composables/useAssetsAPI'
    import useRefreshAPI                from '@/composables/useRefreshAPI'
    import usePermissionsAPI            from '@/composables/usePermissionsAPI'

    // import enumerations
    import SysConfigs                   from '@/configuration/form-options.json'
    import Typology                     from '@/configuration/assetTypology.json'
    import AdministrativeGroups         from '@/configuration/adminGroups.config.json'
    import * as XLSX                    from 'xlsx-js-style'   

    // pdf converter  install as npm install --save dom-to-pdf
    import domToPdf                     from 'dom-to-pdf'

    export default {
        components: {
           EditAsset,
           ViewAssetEntire,
           SemipolarSpinner
        },
        props: [
            'claims',
            'gat'
        ],

        emits: [
        ],
    
        setup(props)    {
            
            const store                                     =   useStore()
            const { checkStoreForRefresh }                  =   useRefreshAPI()
            const { checkAdminGroups }                      =   usePermissionsAPI() 

            //  store data
            const user                  =   computed( () => store.state.user)

            //  actions on store
            const setUser               = (u)    => store.dispatch('setUser', u)

            // security & permissions
            const sysConfigs            =   ref([])
            sysConfigs.value            =   SysConfigs

            const aAssetTypology        =   ref([])             // configuration typology for assets
            const aCIMandatory          =   ref([])             // mandatory CI properties policy

            const AdminGroups           =   ref([])
            AdminGroups.value           =   AdministrativeGroups['securityGroups']
            const aOrgs                 =   ref(Array.prototype.slice.call(user.value['allowed-orgs']))

            // use refs
            const units                 =   ref([])             // units list from config
            const unit                  =   ref({})             // selected unit
            const assets                =   ref([])             // list of assets
            const asset                 =   ref({})             // individual asset to bew viewed/edited
            const assetCount            =   ref(0)
            const selectedAssets        =   ref([])             // listed pre-depoyment assets for employee
            const selectedAsset         =   ref({})             // selected asset from list of selected Assets
            const viewIndexUnit         =   ref(0)              // unit
            const viewIndex             =   ref(0)              // asset
            const viewIndexSelected     =   ref(0)              // selected asset
            const orgDisplay            =   ref(null)           // orgcode display for selection
            const renderState           =   ref('SELECT-UNIT')
            const bAssetsLoaded         =   ref(false)          // takes a long! time to pull in 15,000 assets

            // pull units from unit typology in configuration/form-options
            // Structure:
            //      'unit'
            //      'unit-id'
            //      'short-name'
            //      'listed-stewards'  // FABWeb+ listed stewards for unit

            units.value = Typology["units"]

            // asset - typology characteristics from configuration form-options
            // Structure of JSON typology record:
            //      type:           listable type of asset
            //      ci:             Boolean for configuration item
            //      wipeable:       Boolean for whether asset has disk/storage that requires wiping on surplus
            //      dep-category:   Depreciation category (property useful period)
            //      audit-cat:      State of Illinois audit category
            //      aliases[]:      Array of names used to identify type from description (typically on imports)

            aAssetTypology.value    =   Typology['asset-typology']
            aCIMandatory.value      =   Typology['ci-policy']
 
            const unitFilters       =   ref({
                'unit':             "",
                'short-name':       "",
                'unit-id':          ""
             })

            const assetFilters           =   ref({
                'steward':              "",
                'steward-netid':        "",
                'tag':                  "",
                'asset-type':            "",
                'owner-org':            "",
                'it-data':              "",
                'serial-no':            "",
                'description':          "",
                'mfg':                  "",
                'model':                "",
                'os-type':              ""
            })

         
            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            checkAdminGroups(props.claims)

            orgDisplay.value = user.value['allowed-orgs'].toString()

            const filteredUnits = computed( () => {

                let aFilteredValues = units.value
                aFilteredValues = aFilteredValues.filter( (el) => {
                    return el['unit'].toLowerCase().match(unitFilters.value['unit'].toLowerCase())
                })
                aFilteredValues = aFilteredValues.filter( (el) =>  {
                    return el['short-name'].toLowerCase().match(unitFilters.value['short-name'].toLowerCase())
                })
                aFilteredValues = aFilteredValues.filter( (el) =>  {
                    return el['unit-id'].match(unitFilters.value['unit-id'])
                })           
                return aFilteredValues 
            })


            const filteredAssets = computed( () => {

                let aFilteredValues = assets.value
                aFilteredValues = aFilteredValues.filter( (asset) => {
                    return asset['tag'].toLowerCase().match(assetFilters.value['tag'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['asset-type'].toLowerCase().match(assetFilters.value['asset-type'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['owner-org'].toLowerCase().match(assetFilters.value['owner-org'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['it-data'].toLowerCase().match(assetFilters.value['it-data'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['serial-no'].toLowerCase().match(assetFilters.value['serial-no'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['description'].toLowerCase().match(assetFilters.value['description'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['mfg'].toLowerCase().match(assetFilters.value['mfg'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['model'].toLowerCase().match(assetFilters.value['model'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['os-type'].match(assetFilters.value['os-type'].replace("*","\\*"))
                })
                return aFilteredValues
            })

            const filteredSelectedAssets = computed( () => {

                let aFilteredValues = selectedAssets.value
                aFilteredValues = aFilteredValues.filter( (asset) => {
                    return asset['tag'].toLowerCase().match(assetFilters.value['tag'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['asset-type'].toLowerCase().match(assetFilters.value['asset-type'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['owner-org'].toLowerCase().match(assetFilters.value['owner-org'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['it-data'].toLowerCase().match(assetFilters.value['it-data'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['serial-no'].toLowerCase().match(assetFilters.value['serial-no'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['description'].toLowerCase().match(assetFilters.value['description'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['mfg'].toLowerCase().match(assetFilters.value['mfg'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['model'].toLowerCase().match(assetFilters.value['model'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['os-type'].match(assetFilters.value['os-type'].replace("*","\\*"))
                })
                return aFilteredValues
            })

            return {user, units, unit, assets, asset, assetCount, selectedAssets, filteredSelectedAssets, selectedAsset,
                    filteredUnits, unitFilters, filteredAssets, assetFilters, sysConfigs, aAssetTypology, aCIMandatory,
                    orgDisplay, setUser, AdminGroups, aOrgs, viewIndexUnit, viewIndex, viewIndexSelected, 
                    bAssetsLoaded, renderState}
        },

        data() {
 
            return {
                bNoAssetsFound: false,
                bRecoveryComplete: false,
            }
        },
        methods: {
            // Function: converts status enumeration to more readable 
            prettyType(s) {
                if (!s) return ""
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i].charAt(0).toUpperCase() + aW[i].slice(1);
                }
                return aW.join(' ')
            },

            // FUNCTION: takes the employee on the list using the v-for indices and renders entire form
            // Function is member of action list with (employee, idx) argument
            async selectUnit(_, idx) {

                const { assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset } = useAssetsAPI()

                this.viewIndexUnit = idx
                this.unit = this.filteredUnits[idx]
                this.renderState = 'LIST-ISSUED-ASSETS'

                // load up assets list for selection
                await fetchAssetsOnQuery({'steward-netid': this.unit['unit-id']})
                .then( () => {
                    let rawAssets = assetResults.value
                    // add selection property to array
                    this.assets = rawAssets.map(o => ({...o, ...{"selected": false}}))  
                    return this.assets                       
                })
                .then( () =>    {
                   
                    if(this.assets.length == 0) {
                        this.bNoAssetsFound = true
                    }
                    else {
                        this.bAssetsLoaded = true
                    }
                })
            },
            resetUnitSelection() {
                this.viewIndexUnit = 0
                this.renderState = 'SELECT-UNIT'
                // reset assets
                this.selectedAssets = []
                this.assets = []
            },
            // FUNCTION: takes indexed assets, slices from available assets and inserts into deployed assets table
            selectAsset(_, idx) {
                // alternate button to select an asset
                this.filteredAssets[idx].selected = true
           },
            useSelectedAssets()     {
                this.renderState = 'LIST-SELECTED-ASSETS'
                this.selectedAssets = this.filteredAssets.filter((a) => a.selected == true)
                this.selectedAssets.forEach( (a) => {
                    // temporarily load the steward for viewing only
                    a.steward = "pending for "+this.unit['short-name']
                })
                this.clearAssetFilters()
            },
            viewSelectedAsset(asset, idx) {
                this.viewIndexSelected = idx
                this.renderState = 'VIEW-SELECTED-ASSET'
            }, 
            viewIssuedAsset(asset, idx) {
                this.viewIndex = idx
                this.renderState = 'VIEW-ISSUED-ASSET'
            }, 
            unselectAssets() {
                this.assets.forEach( (a) => {
                    a.selected = false
                })
                this.renderState = 'LIST-ISSUED-ASSETS'
                this.selectedAssets = []
                this.clearAssetFilters()
            },
            backToAssetList() {
                this.renderState    =   "LIST-ISSUED-ASSETS"  
                this.savedFlag      =   false  
            },
            backToSelectedAssetList() {
                this.renderState    =   "LIST-SELECTED-ASSETS"  
                this.savedFlag      =   false  
            },

            recoverAssets()   {

                const {assetResults, assetKeyData, fetchAssetsOnQuery, storeAsset} = useAssetsAPI()

                let bSuccess = true
                this.bAssetsLoaded = false

                // update the required ownership data
                for(let i=0; i<this.filteredSelectedAssets.length; i++) {
                    this.selectedAssets[i]['fk-employee-id']    =   null
                    this.selectedAssets[i]['steward']           =   "recovered"
                    this.selectedAssets[i]['steward-netid']     =   ""
 
                    //mark asset as recovered.  Use disposition to determine what to do next
                    this.selectedAssets[i]['status']            =   "Removed-from-Service"
                  

                    storeAsset(this.filteredSelectedAssets[i])
                    .then( async () => {
                        await fetchAssetsOnQuery({id: assetKeyData.value[0].pkey}) 
                        .then ( () => {
                            this.filteredSelectedAssets[i] = assetResults.value[0]
                            return assetResults.value[0]
                        }) 
                        .then( () => {
                            this.bAssetsLoaded = true
                        })
                        .catch( () =>   {
                            bSuccess = false
                        }) 
                    })
                    .catch( () =>   {
                        bSuccess = false
                    })
                    
                }
                this.bRecoveryComplete = bSuccess    
            },
            // FUNCTION: Disregards any edits and retores the agreement to a new agreeement (PENDING) state
            //
            cancelDeployment()   {               
                this.renderState = 'SELECT-UNIT'
                this.bAssetsSelected = false
            },
           
              // MARK:  THE FOLLOWING METHODS ARE APPLIED TO VIEW/EDIT ELEMENTS TO RETURN TO OVERALL LIST - GENERAL USERS
            
            // FUNCTION: increments through viewing index (experimental)
            nextAsset() {
                this.viewIndex++
                if (this.viewIndex >= this.filteredAssets.length) this.viewIndex = 0
                this.savedFlag      =   false  
            },
            nextSelectedAsset() {
                this.viewIndexSelected++
                if (this.viewIndexSelected >= this.filteredSelectedAssets.length) this.viewIndexSelected = 0
                this.savedFlag      =   false  
            },
            async getAssetList() {

            },
            
            setFilters() {
                // deprecated for computed proxy in setup
            },
            clearUnitFilters()  {
                // reset the filters themselves
                this.filters =  {
                    'unit':             "",
                    'short-name':       "",
                    'unit-id':          ""
               }
            },
            clearAssetFilters() {
                this.assetFilters = {
                    'steward':              "",
                    'steward-netid':        "",
                    'tag':                  "",
                    'asset-type':           "",
                    'owner-org':            "",
                    'it-data':              "",
                    'serial-no':            "",
                    'description':          "",
                    'mfg':                  "",
                    'model':                "",
                    'os-type':              ""
                }
            },
            // wrapper
            resetAssetFilters() {
                this.clearAssetFilters()
                this.renderState = 'SELECT-UNIT'
            },

            // FUNCTION: Created a PDF of ViewEditEmployee rendering
            // id of element on DOM is '_pdf_render'
            // Need to created from the DOM and then do a standard download so that the PDF appears
            // in the browser downloads.
            async generatePDF() {
                
                // MARK:  generate the appropriate filename with embedded date
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                 // 20240404_1559 format
                const filedate = year+month+day+'_'+hours+mins
                const pdfFileName = this.unit['short-name']+"-Unit-Record-"+filedate+".pdf"    

                // select and the container and add a title for the PDF document              
                var element = document.getElementById('_pdf_render');
                var title   = document.createElement("p")
                var brk     = document.createElement("br")
                title.textContent = "Unit Record"
                title.style['font-family'] = "'Times New Roman',Times, serif"
                title.style['font-size'] = "16pt"
                title.style['font-weight'] = "bold"
                title.style['text-align'] = "center"
                title.appendChild(brk)
                title.appendChild(brk)
                var firstChild = document.getElementById("_pdf_row1")
                element.insertBefore(title,firstChild)

                var options = {
                    filename: pdfFileName,
                    excludeTagNames: ['button']
                };
                await domToPdf(element, options, function(pdf) {
                     
                    title.remove()
                });
                    
            }

          
        },      // end of methods list


        // hooks
        beforeMount() {
        },
        mounted() {
         },
        updated() {
        }
    }
</script>

<style scoped>

._master_container {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 50%;
        margin: 25px 0 0 0;
        padding: 0;
        overflow-x: auto;
    }
    ._spinner {
        min-height: 40vh;
        width: 40vw;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 45px auto;
        text-align: center;
    }
    ._list_container {
        width: 98%;
        overflow-x: auto;
        overflow-y: auto;
        margin: 0 5px;;
        padding: 0 5px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: white;
    }
    ._list {
        width: 100%;
        margin: 0;
        padding: 0;
    }
    ._p_selected_name {
        margin: 0 0 10px 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 1.0em;
        font-weight: bold;
    }
    ._p_instruction_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 1.0em;
        font-weight: bold;
        text-align: center;
    }
    ._p_patience {
        width: 30%;
        font-size: 1em;
        color: #13294B;
        text-align: center;
        padding: 2px;
        margin: 2px auto;
    }

    ._p_title_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 10pt;
        font-weight: bold;
        text-align: center;
    }

    ._p_list_header {
        margin: 0;
        color: #13294B;
        font-size: 0.9em;
        font-weight: bold;
        text-align: center;
    }

    /* list action button styling */
    ._action_button {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._action_button:hover {
        background-color: #FF5F05;
    }
    ._action_button_remove {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:red;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._action_button_remove:hover {
        background-color: white;
        color: red;
    }
    ._instructions {
        width: 80%;
        margin: 25px auto;
        padding: 2px;
     }
    ._p_instructions {
        text-align: justify;
        color: #13294B;
        margin: 0;
        padding: 2px;
    }
   
    ._page_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 15px 0 20px 0;
        padding: 0 5px;;
    }
    ._page_inline_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0 5px;
    }
    ._check {    
        margin: 0;
        padding: 0 6px;
    }
    ._check_label {
        margin: 0;
        padding: 0 6px;
    }
    /* unit list styling */
    ._scrollable_list_container {
        width: 100%;
        height: 500px;
        overflow: auto;
        margin: 0;
        padding: 0;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: white;
    }
    ._table {
        display: table;
    }
    ._table_fixed_row   {
        display: table-row;
        position: sticky;
        background-color:#BFC9D9;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: #13294B;
        height: 25px;
        white-space: nowrap;

    }
    ._table_row   {
        background-color:#a4b9dc;        
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        height: 25px;
        white-space: nowrap;
    }
    ._table_row:nth-child(2n+2) {
        background-color:#BFC9D9;
    }
    ._table_cell  {
        display: table-cell;
        min-width: 10%;
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 30px;
    }
    ._table_cell_ellipsis {
        /* display: table-cell; */
        max-width: 80%;
        text-overflow: ellipsis;
        white-space: nowrap;        
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 10px;
        
    }
    ._actions_table_cell {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: center;
        margin: 0 auto;
    }    
    
    ._table_link_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        font-weight: normal;
        color: #13294B;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 50px;
    }
    ._table_link_cell:hover  {
        font-weight: bold;
        color: #FF5F05;
    }
    ._employee_name_tab_cell {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
    }
    ._employee_name_tab_cell:hover {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
        font-weight: normal;
        color: #13294B;
    }
    ._p_column_headers {
        font-size: 1.0em;
        font-weight: bold;
        color: #13294B;
        text-align: center;
        margin: 2px 0;
        padding: 5px;
    }
 
    ._units_warning {
        text-align: center;
        color: #FF5F05;
        margin: 25px auto;
        padding: 5px;
    }

    ._issue_notice {
        float: right;
        margin: 0 15ps 0 0;
        padding: 0 2px;
        color: #FF5F05;
    }
    ._assets_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 0 5px;
    }
    ._assets_inline_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        /* display: inline; */
        margin: 25px 0 0 0;
        padding: 0 5px;
    }
    ._no_assets_warning_container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 20px;
        margin: 10px 0;
    }
    ._assets_warning {
        color: #FF5F05;
    }
    _._assets_warning_clear {
        float: right;
    }    
    
    /* unit list styling */
    ._table {
        display: table;
        margin: 0;
    }
    ._table_check_cell {
        display: table-cell;
        min-width: 10%;
        text-align: center;
        margin: 0;
        padding: 2px 0 2px 0;
    }
    ._table_cell_ellipsis {
        /* display: table-cell; */
        max-width: 80%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 10px;
        
    }
    ._input_filter {
        width: 70%;
        background-color: white;
        color: black;
        font-size: 0.9em;
        font-weight: normal;
        border-style: solid;
        border-radius: 3px;
        border-width: 1px;
        border-color: #13294B;
        margin: 1% 15% 0;
        padding: 2px;
    }
 
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._button_clear_filters {
        background-color:#13294B;
        color: white;
        font-size: 0.9em;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 0 5px;
        padding: 0 5px;
    }

    ._button_clear_filters:hover  {
        background-color: #FF5F05;
        color: white;
    }   
    ._p_saved {
        font-size: 16pt;
        color: #FF5F05;
        padding: 2px;
    }
    ._saved_right {
        padding: 0;
        margin: 0 10px 0 0;
        float: right;
    }
    ._saved_center {
        padding: 0;
        margin: 3px 0;
        text-align: center;
    }
    ._p_templated    {
        padding: 0;
        margin: 0;
    }
    ._p_template {
        margin: 5px auto 5px;
        padding: 0;
        color: #13294B;
        font-size: 12pt;
    }
    ._import_fieldset {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 0;
        padding: 0;
    }
    ._row {
        width: 70%;
        margin: 0 auto;
        text-align: center;
    }
    ._radio {
        display: inline-block;
        width: 16px;
        margin: 0;
        /* position: relative; 
        top: 2px; 
        */
    }
    ._item_label {
        display: inline-block;
        width: 100%; 
        font-size: 0.75em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        margin: 0;
        padding: 0 0 0 10px;
    }
    ._col_30 {
        display: inline-block;
        width: 30%;
        margin: 0;
        padding: 0;
    }
    ._col_33 {
        display: inline-block;
        width: 33%;
        margin: 0;
        padding: 0;
    }
    ._col_67 {
        display: inline-block;
        width: 67%;
        margin: 0 auto;
        padding: 0;
    }
    ._col_70 {
        display: inline-block;
        width: 70%;
        margin: 0 auto;
        padding: 0;
    }
    /* sub-columns for radio buttons and labels */
    ._scol_15 {
        display: inline-block;
        width: 15%;
        margin: 0;
        padding: 0;
    }
    ._scol_25 {
        display: inline-block;
        width: 25%;
        margin: 0;
        padding: 0;
    }
    ._scol_80 {
        display: inline-block;
        text-align: left;
        width: 80%;
        margin: 0;
        padding: 0;
    }
    ._scol_85 {
        display: inline-block;
        text-align: left;
        width: 85%;
        margin: 0;
        padding: 0;
    }
     /* sub-columns for enclosing column radio buttons and labels */
    ._scol_33 {
        display: inline-block;
        text-align: left;
        width: 33%;
        margin: 0 auto;
        padding: 0;
    }
    @media screen and (max-width: 800px) {
        ._import_fieldset {
            flex-wrap: wrap;  
        }
        ._col_30 {
            width: 100%;
        }
        ._col_33 {
            width: 80%;
        }  
        ._col_67 {
            width: 100%;
        }
        ._col_70 {
            width: 80%;
            margin: 0 auto;
        }  
    }
    @media screen and (max-width: 620px) {
        ._col_33 {
            width: 100%;
            margin-top: 0;
        }
       ._scol_25 {
            width: 100%;
            text-align: center;
        }
        ._scol_33 {
            width: 100%;
            text-align: center;
        }

        ._col_67 {
            width: 50%;
            margin: 0 auto;
        }  
        
    }
</style>